import React, { ReactElement, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';
import { Button } from '@ui/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';
import { REFRESH_TOKEN_STORAGE_KEY } from '@app/constants/app/storage';
import { useSetPassword } from '@data';

import { usePromptsWaiting } from './promptQueue';

interface PromptForm {
  next: string;
  confirm: string;
}

const ASKED_KEY = 'phill-set-password-asked';

/**
 * Which link is open, as a number rather than any part of the token itself.
 *
 * The prompt is asked once per link, so it needs to recognise the link it already asked about. The token
 * is a credential, and while it is already in this storage, writing a second copy of it under a key of
 * our own would spread it further for no reason. A hash recognises the same link and is worth nothing to
 * anyone who reads it.
 */
const linkFingerprint = (): string | null => {
  try {
    const token = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    if (!token) return null;
    let hash = 0;
    for (let i = 0; i < token.length; i += 1) hash = (hash * 31 + token.charCodeAt(i)) % 2147483647;
    return hash.toString(36);
  } catch {
    // Storage unavailable (private mode, blocked cookies). Asking again is the safe way to be wrong.
    return null;
  }
};

const alreadyAsked = (): boolean => {
  const fingerprint = linkFingerprint();
  try {
    return fingerprint !== null && localStorage.getItem(ASKED_KEY) === fingerprint;
  } catch {
    return false;
  }
};

const rememberAsked = (): void => {
  const fingerprint = linkFingerprint();
  try {
    if (fingerprint) localStorage.setItem(ASKED_KEY, fingerprint);
  } catch {
    // Nothing to remember it in, so it asks again next time. Better than not asking at all.
  }
};

/**
 * Asks a client to choose a password the first time they open the app without one.
 *
 * A client account is created with no password and reaches the app through the magic link in a report,
 * so this is the only moment they are both reachable and identified. Nothing is emailed on their behalf
 * any more, which makes this the path onto the account rather than a reminder about one.
 *
 * Dismissible, and that is deliberate: they came to read a report, and a wall in front of it would be
 * paid for by the person who only ever wanted the report.
 *
 * Asked once per link rather than once per visit. A client who has said no has answered, and asking again
 * every time they open the same report turns a suggestion into nagging. The next report brings a new link
 * and a fresh ask, which is the point at which it is worth raising again.
 */
const SetPasswordPrompt = (): ReactElement | null => {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(alreadyAsked);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const { mutate: setPassword, isPending } = useSetPassword();
  // Queued behind whatever the client came here to answer. Nothing is remembered while waiting: this is
  // not an answer, so the ask is still owed once the way is clear.
  const waiting = usePromptsWaiting();
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<PromptForm>({ defaultValues: { next: '', confirm: '' } });

  const onSubmit = ({ next }: PromptForm): void => {
    setFailed(null);
    setPassword(
      { password: next },
      {
        onSuccess: () => setDone(true),
        onError: err => {
          const reason = (err as { response?: { data?: { reason?: string } } })?.response?.data?.reason;
          // Already having one is not a failure worth a red line: the account is in the state the
          // dialog was asking for, so it just goes.
          if (reason === 'passwordAlreadySet') setDone(true);
          else setFailed(t('setPasswordPrompt.errors.failed'));
        },
      },
    );
  };

  const dismiss = (): void => {
    rememberAsked();
    setDismissed(true);
  };

  const required = t('setPasswordPrompt.errors.required');

  return (
    <Dialog open={!waiting && !dismissed && !done} onOpenChange={next => !next && !isPending && !waiting && dismiss()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('setPasswordPrompt.title')}</DialogTitle>
          <DialogDescription>{t('setPasswordPrompt.description')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Controller
            name="next"
            control={control}
            rules={{ required, minLength: { value: 8, message: t('setPasswordPrompt.errors.min') } }}
            render={({ field }) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="set-password-new">{t('setPasswordPrompt.new')}</Label>
                <Input id="set-password-new" type="password" autoComplete="new-password" autoFocus {...field} />
                {errors.next && <p className="text-sm text-destructive">{errors.next.message}</p>}
              </div>
            )}
          />

          <Controller
            name="confirm"
            control={control}
            rules={{ required, validate: value => value === watch('next') || t('setPasswordPrompt.errors.mismatch') }}
            render={({ field }) => (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="set-password-confirm">{t('setPasswordPrompt.confirm')}</Label>
                <Input id="set-password-confirm" type="password" autoComplete="new-password" {...field} />
                {errors.confirm && <p className="text-sm text-destructive">{errors.confirm.message}</p>}
              </div>
            )}
          />

          {failed && <p className="text-sm text-destructive">{failed}</p>}

          <div className="flex items-center gap-3">
            <Button type="submit" loading={isPending}>
              {t('setPasswordPrompt.save')}
            </Button>
            <Button type="button" variant="ghost" onClick={dismiss} disabled={isPending}>
              {t('setPasswordPrompt.notNow')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SetPasswordPrompt;
